import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ChatBubbleLeftEllipsisIcon, HandThumbDownIcon, HandThumbUpIcon } from "@heroicons/react/24/outline";
import { CheckCircleIcon, ClipboardDocumentCheckIcon } from "@heroicons/react/20/solid";
import { getMyTasks, postQuestionVote } from "../../../services/questionService";
import { byQuestions, taskTitle } from "../../../components/QuestionFolders/tasks/taskModel";
import { getApiErrorMessage } from "../../../utils/apiError";
import { focusRing, linkButtonClass } from "../../Exams/TestBuilder/ui";
import CommentField from "./CommentField";
import { cleanComment } from "./reviewUtils";

const VOTES = { up: "UPVOTED", down: "DOWNVOTED" };
const CHOICE = { UPVOTED: "up", DOWNVOTED: "down" };
// Questions no review task counts.
const NOT_REVIEWED = ["DRAFT", "ARCHIVED"];

// The reader's review task in work that counts this question (it names it, or its author), or null: its vote on
// the question is what the task waits for.
function useReviewTask(question) {
  const [task, setTask] = useState(null);
  const uuid = question.uuid;
  const folderUuid = question.folder?.uuid;
  const authorUuid = question.author?.uuid;
  const counted = !!folderUuid && !NOT_REVIEWED.includes(question.status?.code);
  useEffect(() => {
    setTask(null);
    if (!counted) return undefined;
    const controller = new AbortController();
    getMyTasks({ done: false, signal: controller.signal })
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.results || [];
        const covers = (item) =>
          byQuestions(item) ? item.questions.some((q) => q.uuid === uuid) : (item.authors || []).some((a) => a.uuid === authorUuid);
        setTask(list.find((item) => item.kind === "review" && !item.cancelled && item.folder?.uuid === folderUuid && covers(item)) || null);
      })
      .catch(() => {});
    return () => controller.abort();
  }, [uuid, folderUuid, authorUuid, counted]);
  return task;
}

/**
 * A question's like and dislike, for those who may vote on it (a subject's experts, moderators and admins; outside
 * folders whoever has vote_question): one press saves it (POST vote/), a press on the other changes it. A comment
 * goes with the vote (the server keeps it on the vote, so the question's history shows it, to its author too).
 * The counts show how the reviewers see it; the vote is advice, the decision stays the moderator's. `onVoted`
 * reads the question's history again. A question of the reader's review task says so: the vote is what counts it.
 */
export default function VoteBar({ question, onVoted }) {
  const { t } = useTranslation();
  const [state, setState] = useState(() => ({ up: question.up_count || 0, down: question.down_count || 0, mine: question.evaluation?.code || "NONE" }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [commenting, setCommenting] = useState(false);
  const [comment, setComment] = useState("");
  const [commentError, setCommentError] = useState("");
  const fieldRef = useRef(null);
  const openerRef = useRef(null);
  // The field closed: "Leave a comment" takes the focus back once it is drawn again.
  const [refocus, setRefocus] = useState(false);
  const task = useReviewTask(question);
  // Another read of the question (a decision, a reload) brings the server's counts.
  useEffect(() => {
    setState({ up: question.up_count || 0, down: question.down_count || 0, mine: question.evaluation?.code || "NONE" });
  }, [question.up_count, question.down_count, question.evaluation?.code]);
  useEffect(() => {
    if (commenting) fieldRef.current?.focus();
    else if (refocus) {
      openerRef.current?.focus();
      setRefocus(false);
    }
  }, [commenting, refocus]);

  // `text`: a comment sent with it (the same vote may go again just to carry one).
  const send = async (choice, text = "") => {
    const before = state;
    const changes = state.mine !== VOTES[choice];
    if (saving || (!changes && !text)) return false;
    // At once on the screen; put back if the server refuses.
    if (changes) {
      setState({
        up: before.up + (choice === "up" ? 1 : 0) - (before.mine === "UPVOTED" ? 1 : 0),
        down: before.down + (choice === "down" ? 1 : 0) - (before.mine === "DOWNVOTED" ? 1 : 0),
        mine: VOTES[choice],
      });
    }
    setSaving(true);
    setError("");
    setCommentError("");
    try {
      await postQuestionVote(question.uuid, text ? { vote: choice, comment: text } : { vote: choice });
      onVoted?.();
      return true;
    } catch (err) {
      setState(before);
      const tooLong = Array.isArray(err?.response?.data?.comment) ? err.response.data.comment[0] : "";
      if (tooLong) setCommentError(tooLong);
      else setError(getApiErrorMessage(t, err, "question_detail.vote_bar.failed"));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const sendComment = async () => {
    const text = cleanComment(comment);
    if (!text || !CHOICE[state.mine]) return;
    if (await send(CHOICE[state.mine], text)) {
      toast.success(t("question_detail.vote_bar.comment_sent"));
      setComment("");
      setCommenting(false);
      setRefocus(true);
    }
  };

  const button = (choice, Icon, label, count, tone) => {
    const on = state.mine === VOTES[choice];
    return (
      <button
        type="button"
        onClick={() => send(choice)}
        aria-pressed={on}
        disabled={saving}
        className={`inline-flex h-10 items-center gap-2 rounded-lg px-3.5 text-sm font-medium ring-1 ring-inset transition duration-200 disabled:cursor-wait max-sm:h-11 ${focusRing} ${
          on ? tone : "bg-white text-slate-700 ring-slate-300 hover:bg-slate-50"
        }`}
      >
        <Icon className="h-4 w-4" aria-hidden="true" />
        {label}
        <span className="tabular-nums opacity-80">{count}</span>
      </button>
    );
  };

  const voted = !!CHOICE[state.mine];
  return (
    <div className={`rounded-xl border p-4 sm:p-5 ${task && !voted ? "border-blue-300 ring-2 ring-blue-100" : "border-slate-200"}`}>
      {task && (
        <p
          className={`mb-3 flex items-start gap-2 rounded-lg px-3 py-2 text-sm ring-1 ring-inset ${
            voted ? "bg-emerald-50 text-emerald-900 ring-emerald-600/20" : "bg-blue-50 text-blue-900 ring-blue-600/20"
          }`}
        >
          {voted ? (
            <CheckCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
          ) : (
            <ClipboardDocumentCheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" aria-hidden="true" />
          )}
          <span>{t(voted ? "question_detail.vote_bar.task_counted" : "question_detail.vote_bar.task_todo", { task: taskTitle(t, task) })}</span>
        </p>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h4 className="text-sm font-semibold text-slate-900">{t("question_detail.vote_bar.title")}</h4>
        <div className="flex flex-wrap gap-2">
          {button("up", HandThumbUpIcon, t("question_detail.vote_bar.like"), state.up, "bg-emerald-50 text-emerald-800 ring-emerald-600/30")}
          {button("down", HandThumbDownIcon, t("question_detail.vote_bar.dislike"), state.down, "bg-red-50 text-red-800 ring-red-600/30")}
        </div>
      </div>
      <p className="mt-2 text-xs text-slate-500">{t("question_detail.vote_bar.hint")}</p>
      {error && (
        <p role="alert" className="mt-2 text-sm text-red-700">
          {error}
        </p>
      )}

      {commenting ? (
        <div className="mt-4 space-y-3 border-t border-slate-100 pt-4">
          <CommentField
            ref={fieldRef}
            id={`vote-comment-${question.uuid}`}
            label={t("question_detail.vote_bar.comment_label")}
            placeholder={t("question_detail.vote_bar.comment_placeholder")}
            value={comment}
            onChange={(value) => {
              setComment(value);
              setCommentError("");
            }}
            error={commentError}
            disabled={saving}
          />
          {!voted && <p className="text-xs font-medium text-amber-700">{t("question_detail.vote_bar.comment_needs_vote")}</p>}
          <div className="flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setCommenting(false);
                setCommentError("");
                setRefocus(true);
              }}
              disabled={saving}
              className={`inline-flex h-10 items-center rounded-lg px-3 text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 max-sm:h-11 ${focusRing}`}
            >
              {t("buttons.cancel")}
            </button>
            <button
              type="button"
              onClick={sendComment}
              disabled={saving || !voted || !cleanComment(comment)}
              className={`inline-flex h-10 items-center rounded-lg bg-blue-600 px-4 text-sm font-medium text-white shadow-sm transition duration-200 hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 max-sm:h-11 ${focusRing}`}
            >
              {t("question_detail.vote_bar.comment_send")}
            </button>
          </div>
        </div>
      ) : (
        <button ref={openerRef} type="button" onClick={() => setCommenting(true)} className={`${linkButtonClass} mt-3 max-sm:min-h-11`}>
          <ChatBubbleLeftEllipsisIcon className="h-4 w-4" aria-hidden="true" />
          {t("question_detail.vote_bar.comment_open")}
        </button>
      )}
    </div>
  );
}
